import { useEffect, useState, useCallback, useRef } from "react";
import { Loader2, Star, Inbox, Clock, Trophy, Send, MessageSquare, Pencil, Info, FileText, Trash2, ChevronDown, Anchor, CalendarDays, ListChecks, AlertTriangle, X } from "lucide-react";
import { api, fmtErr } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import FileUploader from "../../components/FileUploader";
import { VatBreakdown } from "./maintenance/components";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { SupplierForm } from "./MarketplaceAdmin";
import { ReviewReplyForm } from "../../components/SupplierReview";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";

// Collapsed-by-default section (Won / Declined jobs) — header click toggles.
// New requests & Awaiting decision stay always expanded per the owner's rule.
function CollapsedSection({ title, count, children, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="mt-8" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 mb-2 text-left"
        data-testid={`${testid}-toggle`}
      >
        <h2 className="font-display text-xl">{title} ({count})</h2>
        <ChevronDown className={`h-4 w-4 text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`} />
        {!open && <span className="text-[11px] text-zinc-400">click to expand</span>}
      </button>
      {open && children}
    </section>
  );
}

// Read-only "what's behind this request" dialog for suppliers — they don't
// have yacht:read, so we surface the info already carried on the quote.
export function YachtInfoDialog({ q, onClose, onSent }) {
  const c = q.yacht_contact;
  const lead = q.yacht_leadership || {};
  const s = q.yacht_specs || {};
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const send = async () => {
    const text = note.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      await api.post(`/quotes/${q.id}/messages`, { text });
      toast.success(`Message sent to ${c?.name || q.yacht_name || "the yacht"}`);
      setNote("");
      onSent && onSent();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSending(false);
    }
  };
  const people = [
    ["Owner", lead.owner || "—"],
    ["Captain", lead.captain || "—"],
    ["Primary contact", c ? `${c.name}${c.role ? ` (${c.role})` : ""}` : "—"],
  ];
  const model = [s.brand, s.model].filter(Boolean).join(" ");
  const specs = [
    ["Model", model || "—"],
    ["Year", s.year || "—"],
    ["Length", s.length_m ? `${s.length_m} m` : "—"],
    ["Flag", s.flag || "—"],
    ["Home marina", s.home_marina || "—"],
  ];
  const request = [
    ["Category", q.task_category || "—"],
    ["Priority", q.task_priority || "—"],
    ["Due", (q.task_due_date || "").slice(0, 10) || "—"],
  ];
  const Rows = ({ items }) => items.map(([k, v]) => (
    <div key={k} className="flex justify-between gap-3"><span className="text-zinc-500">{k}</span><span className="font-medium text-right">{v}</span></div>
  ));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[calc(100vw-1.5rem)] max-w-md" data-testid="yacht-info-dialog" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader><DialogTitle>{q.yacht_name || "Yacht"}</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Yacht and request information</DialogDescription>
        <div className="space-y-3 text-sm">
          <div className="rounded-lg border border-sky-200 bg-sky-50/60 p-2.5 space-y-1.5" data-testid="yacht-info-people">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-sky-700">People</div>
            <Rows items={people} />
          </div>
          <div className="rounded-lg border border-teal-200 bg-teal-50/60 p-2.5 space-y-1.5" data-testid="yacht-info-specs">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-teal-700">Yacht specs</div>
            <Rows items={specs} />
            <div className="flex justify-between gap-3">
              <span className="text-zinc-500">Engines</span>
              <span className="font-medium text-right">
                {(s.engines || []).length ? s.engines.map((e, i) => <div key={i}>{e}</div>) : "—"}
              </span>
            </div>
          </div>
          <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-2.5 space-y-1.5" data-testid="yacht-info-request">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-amber-700">Request</div>
            <Rows items={request} />
            {q.task_description && <p className="text-sm whitespace-pre-wrap mt-0.5">{q.task_description}</p>}
          </div>
          <div className="rounded-lg border border-indigo-200 bg-indigo-50/60 p-2.5 space-y-1.5" data-testid="yacht-info-message">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-indigo-700">
              Message {c?.name || "the yacht"}
            </div>
            <div className="flex gap-2">
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Write a message…"
                className="h-9 bg-white"
                data-testid="yacht-info-message-input"
              />
              <Button size="sm" className="h-9" onClick={send} disabled={sending || !note.trim()} data-testid="yacht-info-send-btn">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
            <p className="text-[11px] text-zinc-500">Posts into this request's message thread.</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-zinc-400 text-xs uppercase tracking-wider"><Icon className="h-3.5 w-3.5" />{label}</div>
      <div className="font-display text-2xl mt-1">{value}</div>
    </div>
  );
}

// Bid currency: the supplier's local currency plus USD/EUR as alternatives.
const COUNTRY_CURRENCY = {
  "united arab emirates": "AED", uae: "AED", "united states": "USD", usa: "USD",
  "united kingdom": "GBP", uk: "GBP", turkey: "TRY", türkiye: "TRY",
  switzerland: "CHF", norway: "NOK", sweden: "SEK", denmark: "DKK",
  australia: "AUD", "new zealand": "NZD", singapore: "SGD", "hong kong": "HKD",
  thailand: "THB", qatar: "QAR", "saudi arabia": "SAR", bahrain: "BHD",
  kuwait: "KWD", oman: "OMR", maldives: "MVR", seychelles: "SCR",
  mexico: "MXN", brazil: "BRL", canada: "CAD", india: "INR",
  indonesia: "IDR", malaysia: "MYR", japan: "JPY", china: "CNY",
  "south africa": "ZAR", egypt: "EGP", morocco: "MAD", tunisia: "TND",
  israel: "ILS", bahamas: "BSD", fiji: "FJD",
};
export function localCurrencyFor(country) {
  return COUNTRY_CURRENCY[(country || "").trim().toLowerCase()] || "EUR";
}

const PRIORITY_CHIP = {
  urgent: "border-rose-200 bg-rose-50 text-rose-700",
  high: "border-rose-200 bg-rose-50 text-rose-700",
  medium: "border-amber-200 bg-amber-50 text-amber-700",
  low: "border-zinc-200 bg-zinc-50 text-zinc-600",
};

export function RequestCard({ q, onChange, localCurrency = "EUR" }) {
  const { user: authUser } = useAuth();
  const canQuote = authUser?.supplier_permissions?.can_quote !== false;
  // First-responder lock: once a teammate handles the request, other managers
  // can only watch — the admin may still act on their behalf.
  const lockedByTeammate = !!q.handled_by && q.handled_by !== authUser?.id && authUser?.supplier_role === "manager";
  const currencyOptions = [...new Set([localCurrency, "USD", "EUR"])];
  const [form, setForm] = useState({ amount: "", currency: localCurrency, vat: "", notes: "", start_date: "", end_date: "", scope: [{ text: "", price: "" }] });
  const [amountTouched, setAmountTouched] = useState(false);
  const [editing, setEditing] = useState(false);
  // Suppliers may edit steps & pricing until the yacht accepts or declines.
  const canEditQuote = ["quoted", "negotiating"].includes(q.status);
  const [msg, setMsg] = useState("");
  const [showMsg, setShowMsg] = useState(true);
  const [info, setInfo] = useState(false);
  const [busy, setBusy] = useState(false);
  const threadRef = useRef(null);
  const scrollToLatest = () => {
    requestAnimationFrame(() => {
      if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
    });
  };
  useEffect(() => { if (showMsg) scrollToLatest(); }, [showMsg, (q.messages || []).length]);
  // Thread is open by default — clear the unread flag once visible.
  useEffect(() => {
    if (showMsg && (q.unread || 0) > 0) {
      api.post(`/quotes/${q.id}/mark-read`).then(onChange).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const act = async (fn) => { setBusy(true); try { await fn(); onChange(); } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); } };

  // Scope steps: text + optional price per step. The total Amount is the sum
  // of step prices but stays editable — steps don't all need a price.
  const scopeSum = (steps) => steps.reduce((s, it) => s + (Number(it.price) || 0), 0);
  // End price = subtotal + VAT (the saved amount always INCLUDES VAT).
  const grossFor = (steps, vat) => {
    const sub = scopeSum(steps);
    const pct = vat === "" || vat == null ? null : Number(vat);
    return Math.round((pct ? sub * (1 + pct / 100) : sub) * 100) / 100;
  };
  const setScope = (i, patch) => setForm((f) => {
    const scope = f.scope.map((s, idx) => (idx === i ? { ...s, ...patch } : s));
    const sum = grossFor(scope, f.vat);
    return { ...f, scope, amount: !amountTouched && sum > 0 ? String(sum) : f.amount };
  });
  const setVat = (v) => setForm((f) => {
    const sum = grossFor(f.scope, v);
    return { ...f, vat: v, amount: !amountTouched && sum > 0 ? String(sum) : f.amount };
  });
  const addScope = () => setForm((f) => ({ ...f, scope: [...f.scope, { text: "", price: "" }] }));
  const removeScope = (i) => setForm((f) => {
    const scope = f.scope.filter((_, idx) => idx !== i);
    const sum = grossFor(scope, f.vat);
    return { ...f, scope: scope.length ? scope : [{ text: "", price: "" }], amount: !amountTouched && sum > 0 ? String(sum) : f.amount };
  });

  const submit = () => {
    if (!canQuote) { toast.error("Your company admin hasn't allowed you to send offers."); return; }
    if (!form.amount) { toast.error("Enter an amount"); return; }
    return act(async () => {
      const scope_items = form.scope
        .filter((s) => s.text.trim())
        .map((s) => ({ text: s.text.trim(), price: s.price === "" ? null : Number(s.price) }));
      await api.post(`/quotes/${q.id}/submit`, {
        amount: Number(form.amount), currency: form.currency, notes: form.notes || null,
        vat_percent: form.vat === "" ? null : Number(form.vat),
        start_date: form.start_date || null, end_date: form.end_date || null, scope_items,
      });
      toast.success("Quote sent");
    });
  };

  const startEdit = () => {
    const scope = (q.scope_items || []).length
      ? q.scope_items.map((s) => ({ text: s.text || "", price: s.price == null ? "" : String(s.price) }))
      : [{ text: "", price: "" }];
    const vat = q.vat_percent != null ? String(q.vat_percent) : "";
    const sum = grossFor(scope, vat);
    setForm({
      amount: q.amount == null ? "" : String(q.amount),
      currency: q.currency || localCurrency, vat, notes: "",
      start_date: q.start_date || "", end_date: q.end_date || "", scope,
    });
    // Keep auto-sum alive only if the saved total still equals the step sum.
    setAmountTouched(!(sum > 0 && Math.abs(sum - Number(q.amount || 0)) < 0.005));
    setEditing(true);
  };

  const saveEdit = () => act(async () => {
    if (!form.amount) { toast.error("Enter an amount"); throw new Error("amount"); }
    const scope_items = form.scope
      .filter((s) => s.text.trim())
      .map((s) => ({ text: s.text.trim(), price: s.price === "" ? null : Number(s.price) }));
    await api.post(`/quotes/${q.id}/update`, {
      amount: Number(form.amount), currency: form.currency,
      vat_percent: form.vat === "" ? null : Number(form.vat),
      start_date: form.start_date || null, end_date: form.end_date || null, scope_items,
    });
    toast.success(q.status === "negotiating"
      ? "Quote updated — sent to the yacht as your counter offer"
      : "Quote updated — the yacht has been notified");
    setEditing(false);
  });
  const decline = () => act(async () => { await api.post(`/quotes/${q.id}/decline`); });
  const addDoc = (path, name) => act(async () => { await api.post(`/quotes/${q.id}/documents`, { file_path: path, name, kind: "quotation" }); toast.success("Quotation attached"); });
  const send = () => act(async () => { if (!msg.trim()) return; await api.post(`/quotes/${q.id}/messages`, { text: msg.trim() }); setMsg(""); });
  // Sender-side undo: remove own message within 15 min, never once accepted.
  const canRemoveMsg = (m) => !m.deleted && q.status !== "accepted" && Date.now() - new Date(m.at).getTime() < 15 * 60 * 1000;
  const removeMsg = (mid) => act(async () => { await api.delete(`/quotes/${q.id}/messages/${mid}`); });
  const fileUrl = (p) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`;
  const awaitingMe = q.status === "negotiating" && q.awaiting === "supplier";
  // The supplier's quote/counter is sent and now waiting on the customer.
  const sentAwaitingCustomer = q.awaiting === "requester" && (q.status === "quoted" || q.status === "negotiating");

  const unread = q.unread || 0;
  const toggleMsg = async () => {
    const next = !showMsg;
    setShowMsg(next);
    if (next && unread > 0) {
      try { await api.post(`/quotes/${q.id}/mark-read`); onChange(); } catch { /* non-fatal */ }
    }
  };
  const fmtTime = (s) => {
    if (!s) return "";
    try { return new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return ""; }
  };

  // Framed card: colored header band per state (matches the yacht-side quote view).
  const tone = awaitingMe ? "amber" : sentAwaitingCustomer ? "sky"
    : q.status === "invited" ? "rose" : q.status === "accepted" ? "emerald" : "zinc";
  const TONES = {
    amber: ["border-amber-300 ring-1 ring-amber-200", "bg-amber-50 border-amber-100"],
    sky: ["border-sky-300 ring-1 ring-sky-200", "bg-sky-50 border-sky-100"],
    rose: ["border-rose-200", "bg-rose-50/70 border-rose-100"],
    emerald: ["border-emerald-200", "bg-emerald-50/70 border-emerald-100"],
    zinc: ["border-zinc-200", "bg-zinc-50 border-zinc-100"],
  };

  return (
    <div className={`rounded-xl border overflow-hidden transition-colors ${TONES[tone][0]}`} data-testid={`req-${q.id}`} id={`req-${q.id}`}>
      <div className={`flex items-start gap-2 px-3 py-2 border-b ${TONES[tone][1]}`}>
        <div className="min-w-0 flex-1">
          {q.yacht_name && (
            <div className="flex items-center gap-1.5 text-[11px] font-medium text-sky-700 truncate" data-testid={`req-yacht-${q.id}`}>
              <Anchor className="h-3 w-3 shrink-0" />
              <span className="truncate">{q.yacht_name}{q.yacht_contact?.name ? ` · ${q.yacht_contact.name}` : ""}</span>
            </div>
          )}
          <span className="font-semibold text-[15px] text-zinc-900">{q.task_title || "Task"}</span>
        </div>
        {q.yacht_name && (
          <button type="button" onClick={() => setInfo(true)} className="p-1 rounded hover:bg-white/70 text-zinc-500 shrink-0" title="Yacht information" data-testid={`req-info-${q.id}`}>
            <Info className="h-4 w-4" />
          </button>
        )}
        {q.handled_by_name && (
          <span className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-white px-2 py-0.5 text-[10px] font-medium text-indigo-700 shrink-0" data-testid={`req-handledby-${q.id}`}>
            {q.handled_by === authUser?.id ? "You handle this" : `Handled by ${q.handled_by_name}`}
          </span>
        )}
        {q.handled_by && !["accepted", "declined", "rejected"].includes(q.status)
          && (q.handled_by === authUser?.id || authUser?.supplier_role === "admin") && (
          <button
            type="button"
            onClick={async () => {
              try {
                await api.post(`/quotes/${q.id}/release`);
                toast.success("Request released — your teammates can take it now");
                onChange?.();
              } catch (e) { toast.error(fmtErr(e)); }
            }}
            className="text-[10px] underline text-zinc-500 hover:text-zinc-800 shrink-0"
            title="Hand this request back so a teammate can take it"
            data-testid={`req-release-${q.id}`}
          >Release</button>
        )}
        {q.amount != null && <span className="text-sm font-semibold shrink-0 text-emerald-700">{q.currency} {q.amount}</span>}
      </div>
      <div className="p-3">
      {(q.task_category || q.task_priority || (q.task_due_date || "").slice(0, 10)) && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          {q.task_category && (
            <span className="inline-flex items-center rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[10px] font-medium text-indigo-700">{q.task_category}</span>
          )}
          {q.task_priority && (
            <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium capitalize ${PRIORITY_CHIP[(q.task_priority || "").toLowerCase()] || PRIORITY_CHIP.low}`}>
              {q.task_priority} priority
            </span>
          )}
          {(q.task_due_date || "").slice(0, 10) && (
            <span className="inline-flex items-center gap-1 rounded-full border border-zinc-200 bg-white px-2 py-0.5 text-[10px] font-medium text-zinc-600">
              <CalendarDays className="h-3 w-3 text-zinc-400" />due {(q.task_due_date || "").slice(0, 10)}
            </span>
          )}
        </div>
      )}
      {q.task_description && (
        <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50/60 p-3" data-testid={`req-desc-${q.id}`}>
          <div className="text-[11px] uppercase tracking-wider text-amber-800 font-semibold mb-1">Task description</div>
          <p className="text-[13px] leading-relaxed text-zinc-800 whitespace-pre-wrap">{q.task_description}</p>
        </div>
      )}
      {(q.status === "invited" || editing) && (lockedByTeammate ? (
        <div className="mt-3 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs text-zinc-600" data-testid={`req-locked-${q.id}`}>
          {q.handled_by_name} responded first and is handling this request. Only your admin can step in.
        </div>
      ) : (
        <div className="mt-3 rounded-xl border border-sky-100 bg-sky-50/50 p-3 space-y-3" data-testid={`req-quote-form-${q.id}`}>
          <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-sky-800">
            <Pencil className="h-3.5 w-3.5" />{editing ? "Edit your quote" : "Your quote"}
          </div>
          {q.direct_assign && !editing && (
            <Badge className="bg-indigo-50 text-indigo-700 hover:bg-indigo-50 border border-indigo-200">Assigned to you — please send your quote</Badge>
          )}
          {/* Scope steps — text + optional price per step, unlimited rows */}
          <div>
            <div className="flex items-center gap-1.5 text-[11px] font-medium text-zinc-700 mb-1.5">
              <ListChecks className="h-3.5 w-3.5 text-emerald-600" />Scope of work
              <span className="text-zinc-400 font-normal">— one step per row, price optional</span>
            </div>
            <div className="space-y-1.5">
              {form.scope.map((s, i) => (
                <div key={i} className="grid grid-cols-[22px_1fr_110px_26px] gap-2 items-start">
                  <span className="text-xs text-zinc-500 font-medium text-right pt-2" data-testid={`req-scope-num-${q.id}-${i}`}>{i + 1}.</span>
                  <Textarea placeholder={`Scope step ${i + 1}`} rows={3} className="min-h-0 text-sm bg-white" value={s.text} onChange={(e) => setScope(i, { text: e.target.value })} data-testid={`req-scope-text-${q.id}-${i}`} />
                  <Input placeholder="Price" type="number" className="no-spinner bg-white" value={s.price} onChange={(e) => setScope(i, { price: e.target.value })} data-testid={`req-scope-price-${q.id}-${i}`} />
                  <button
                    type="button"
                    onClick={() => removeScope(i)}
                    className={`pt-2 text-zinc-400 hover:text-red-600 ${form.scope.length === 1 && !s.text && !s.price ? "invisible" : ""}`}
                    title="Remove step"
                    data-testid={`req-scope-remove-${q.id}-${i}`}
                  >×</button>
                </div>
              ))}
            </div>
            <button type="button" onClick={addScope} className="mt-2 inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs text-emerald-700 font-medium hover:bg-emerald-100 transition-colors" data-testid={`req-scope-add-${q.id}`}>
              + Add step
            </button>
          </div>
          {/* Total amount (auto-summed from step prices, editable) + currency */}
          <div>
            <div className="grid grid-cols-[1fr_76px_88px] gap-2">
              <div>
                <label className="block text-[11px] font-medium text-zinc-600 mb-1">Total amount <span className="text-zinc-400 font-normal">(incl. VAT)</span></label>
                <Input
                  placeholder="0.00"
                  type="number"
                  className="no-spinner font-semibold bg-white"
                  value={form.amount}
                  onChange={(e) => { setAmountTouched(true); setForm((f) => ({ ...f, amount: e.target.value })); }}
                  data-testid={`req-amount-${q.id}`}
                />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-zinc-600 mb-1">VAT %</label>
                <Input
                  placeholder="0"
                  type="number" min="0" max="100"
                  className="no-spinner bg-white"
                  value={form.vat}
                  onChange={(e) => setVat(e.target.value)}
                  title="VAT in percent — added on top of the scope subtotal"
                  data-testid={`req-vat-${q.id}`}
                />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-zinc-600 mb-1">Currency</label>
                <select
                  value={form.currency}
                  onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}
                  className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                  data-testid={`req-currency-${q.id}`}
                >
                  {currencyOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div className="mt-1 text-[11px] text-zinc-500 leading-snug">Auto-calculated from the scope prices + VAT % — adjust freely if not every step is priced.</div>
          </div>
          {/* Work starts / Work ends */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[11px] font-medium text-zinc-600 mb-1">Work starts</label>
              <Input type="date" className="bg-white" title="Work starts" value={form.start_date} onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))} data-testid={`req-start-date-${q.id}`} />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-zinc-600 mb-1">Work ends <span className="text-zinc-400 font-normal">(optional)</span></label>
              <Input type="date" className="bg-white" title="Work ends (optional)" value={form.end_date} min={form.start_date || undefined} onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))} data-testid={`req-end-date-${q.id}`} />
              <div className="mt-1 text-[10px] text-zinc-400">Shows the job's duration on the yacht's calendar</div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-sky-100">
            {editing ? (
              <>
                <Button size="sm" className="bg-ink hover:bg-black" onClick={saveEdit} disabled={busy} data-testid={`req-save-edit-${q.id}`}>Save changes</Button>
                <Button size="sm" variant="outline" className="bg-white" onClick={() => setEditing(false)} disabled={busy} data-testid={`req-cancel-edit-${q.id}`}>Cancel</Button>
              </>
            ) : (
              <>
                <Button size="sm" className="bg-ink hover:bg-black" onClick={submit} disabled={busy || !canQuote} title={!canQuote ? "Your company admin hasn't allowed you to send offers." : undefined} data-testid={`req-submit-${q.id}`}>Send quote</Button>
                <Button size="sm" variant="outline" className="bg-white" onClick={decline} disabled={busy}>Decline</Button>
              </>
            )}
            <FileUploader yacht_id={q.yacht_id} folder="quotes" accept="application/pdf,image/*" multiple label="Attach quotation" testid={`req-doc-upload-${q.id}`} onUploaded={(path, record) => addDoc(path, record?.original_filename || "Quotation")} />
          </div>
        </div>
      ))}

      {(q.current_amount != null) && (
        <div className="mt-2 text-xs">
          <span className="text-zinc-500">Current offer: </span>
          <span className="font-medium text-zinc-800">{q.current_currency || q.currency} {q.current_amount}</span>
          {q.status === "quoted" && <span className="text-sky-700"> · awaiting the customer's decision</span>}
          {q.awaiting === "supplier" && <span className="text-amber-700"> · your turn to respond</span>}
          {q.awaiting === "requester" && q.status === "negotiating" && <span className="text-zinc-500"> · awaiting customer</span>}
        </div>
      )}

      {(q.quote_documents || []).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2" data-testid={`req-docs-${q.id}`}>
          {q.quote_documents.map((d) => (
            <a key={d.id} href={fileUrl(d.file_path)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] text-sky-700 border border-sky-200 bg-sky-50 rounded px-2 py-0.5 hover:bg-sky-100" data-testid={`req-doc-${d.id}`}>
              <FileText className="h-3 w-3" /> {d.name || "Quotation"}
            </a>
          ))}
        </div>
      )}

      {(q.offers || []).length > 0 && (
        <div className="mt-2 border-l-2 border-zinc-100 pl-2 space-y-0.5" data-testid={`req-offers-${q.id}`}>
          {q.offers.map((o) => (
            <div key={o.id} className="text-[11px] text-zinc-500">
              <span className="font-medium text-zinc-700">{o.by === "supplier" ? "You" : "Yacht"}</span>: {o.currency} {o.amount}{o.note ? ` — ${o.note}` : ""}
            </div>
          ))}
        </div>
      )}

      {awaitingMe && !editing && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {/* Respond to the yacht's counter offer by editing the quote —
              "Save changes" sends the update as your counter automatically. */}
          <Button size="sm" className="bg-ink hover:bg-black" onClick={startEdit} disabled={busy} data-testid={`req-edit-quote-${q.id}`}>
            <Pencil className="h-3 w-3 mr-1" />Edit quote
          </Button>
          <Button size="sm" variant="outline" onClick={decline} disabled={busy}>Decline</Button>
        </div>
      )}
      {/* Supplier sees the scope they submitted (until they open edit mode) */}
      {!editing && q.status !== "invited" && (q.scope_items || []).length > 0 && (
        <div className="mt-2 rounded-md border border-zinc-100 bg-zinc-50/60 p-2" data-testid={`req-scope-view-${q.id}`}>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Your scope</div>
          <ul className="space-y-0.5">
            {q.scope_items.map((s, i) => (
              <li key={i} className="flex items-start justify-between gap-3 text-xs text-zinc-700">
                <span className="min-w-0 whitespace-pre-wrap [overflow-wrap:anywhere]"><span className="text-zinc-400 font-medium mr-1">{i + 1}.</span>{s.text}</span>
                {s.price != null && <span className="text-zinc-500 whitespace-nowrap shrink-0">{q.currency} {s.price}</span>}
              </li>
            ))}
          </ul>
          <VatBreakdown items={q.scope_items} amount={q.amount} currency={q.currency} vatPercent={q.vat_percent} testid={`req-vat-view-${q.id}`} />
          <div className="mt-1 pt-1 border-t border-zinc-200 flex items-baseline justify-between text-xs">
            <span className="text-zinc-500">Total quoted{q.vat_percent != null ? " incl. VAT" : ""}</span>
            <span className="font-medium text-zinc-800">{q.currency} {q.amount}</span>
          </div>
        </div>
      )}
      {q.status === "quoted" && !editing && <div className="mt-1 text-xs text-sky-700">Quote sent — awaiting the customer's decision.</div>}
      {canEditQuote && !editing && !awaitingMe && (
        <Button size="sm" variant="outline" className="mt-2 h-7 px-2 text-xs" onClick={startEdit} disabled={busy} data-testid={`req-edit-quote-${q.id}`}>
          <Pencil className="h-3 w-3 mr-1" />Edit quote
        </Button>
      )}
      {q.status === "accepted" && <Badge className="mt-1 bg-emerald-50 text-emerald-700 hover:bg-emerald-50">Won — job confirmed</Badge>}
      {q.status === "declined" && (
        <Badge
          className={`mt-1 ${q.task_deleted ? "bg-rose-50 text-rose-700 hover:bg-rose-50" : q.cancelled ? "bg-amber-50 text-amber-700 hover:bg-amber-50" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-100"}`}
          data-testid={`req-closed-badge-${q.id}`}
        >
          {q.task_deleted ? "Task deleted by client — kept for your records" : q.cancelled ? "Cancelled by yacht" : "Declined by yacht"}
        </Badge>
      )}
      {q.status === "rejected" && <Badge className="mt-1 bg-zinc-100 text-zinc-600 hover:bg-zinc-100">Not selected — another supplier was chosen</Badge>}
      {["declined", "rejected"].includes(q.status) && (
        <Button
          size="sm"
          variant="ghost"
          className="mt-1 ml-2 h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
          disabled={busy}
          onClick={async () => {
            if (!window.confirm("Remove this declined job from your hub? This can't be undone.")) return;
            setBusy(true);
            try {
              await api.post(`/quotes/${q.id}/supplier-hide`);
              toast.success("Job removed");
              onChange?.();
            } catch (err) { toast.error(fmtErr(err)); }
            finally { setBusy(false); }
          }}
          data-testid={`req-delete-declined-${q.id}`}
        >
          <Trash2 className="h-3.5 w-3.5 mr-1" />Delete
        </Button>
      )}

      <button
        onClick={toggleMsg}
        className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
        data-testid={`req-messages-toggle-${q.id}`}
      >
        <MessageSquare className="h-4 w-4" />
        Messages
        {(q.messages || []).length > 0 && <span className="text-emerald-100">({(q.messages || []).length})</span>}
        {unread > 0 && (
          <span
            className="inline-flex items-center justify-center min-w-[1.1rem] h-4 px-1 rounded-full text-[10px] font-semibold bg-red-600 text-white"
            data-testid={`req-unread-${q.id}`}
          >
            {unread}
          </span>
        )}
      </button>
      </div>
      {showMsg && (
        <div className="border-t border-emerald-100 bg-emerald-50/40 px-3 py-3">
          <div ref={threadRef} className="space-y-2.5 max-h-64 overflow-y-auto pr-1" data-testid={`req-thread-${q.id}`}>
            {(q.messages || []).length === 0 && (
              <p className="text-xs text-zinc-400 text-center py-3">No messages yet — start the conversation with the yacht.</p>
            )}
            {(q.messages || []).map((m) => {
              const mine = m.sender_kind === "supplier";
              return (
                <div key={m.id} className={`flex flex-col ${mine ? "items-end" : "items-start"}`}>
                  <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-xs ${mine ? "bg-sky-600 text-white rounded-br-sm" : "bg-white border border-emerald-100 text-zinc-800 rounded-bl-sm"}`}>
                    <div className={`text-[10px] mb-0.5 font-medium ${mine ? "text-sky-100" : "text-zinc-500"}`}>{m.sender_name}</div>
                    {m.deleted
                      ? <div className="italic opacity-60" data-testid={`req-msg-removed-${m.id}`}>Message removed</div>
                      : <div className="whitespace-pre-wrap break-words">{m.text}</div>}
                  </div>
                  <div className="text-[10px] text-zinc-400 mt-0.5 px-1 flex items-center gap-2">
                    {fmtTime(m.at)}
                    {mine && canRemoveMsg(m) && (
                      <button type="button" onClick={() => removeMsg(m.id)} className="text-zinc-400 hover:text-red-600 underline underline-offset-2" title="Remove this message (within 15 minutes of sending)" data-testid={`req-msg-remove-${m.id}`}>Remove</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {q.task_deleted ? (
            <p className="mt-2.5 text-xs text-rose-600" data-testid={`req-readonly-note-${q.id}`}>The client deleted this task — the thread is read-only.</p>
          ) : (
            <div className="flex gap-2 mt-2.5">
              <Input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Write a message…" className="bg-white" onKeyDown={(e) => e.key === "Enter" && send()} data-testid={`req-msg-input-${q.id}`} />
              <Button size="icon" className="bg-ink hover:bg-black shrink-0" onClick={send} disabled={busy} data-testid={`req-msg-send-${q.id}`}><Send className="h-4 w-4" /></Button>
            </div>
          )}
        </div>
      )}
      {info && <YachtInfoDialog q={q} onClose={() => setInfo(false)} onSent={onChange} />}
    </div>
  );
}

export default function SupplierDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [ibanDismissed, setIbanDismissed] = useState(() => {
    try { return !!localStorage.getItem("iban_nudge_dismissed"); } catch { return false; }
  });
  const load = useCallback(async () => {
    try { const { data } = await api.get("/supplier/dashboard"); setData(data); } catch (e) { toast.error(fmtErr(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="p-10 text-center text-zinc-400"><Loader2 className="h-6 w-6 animate-spin inline" /></div>;
  if (!data.is_supplier) {
    return (
      <div className="p-6 lg:p-10 max-w-2xl" data-testid="supplier-dashboard">
        <h1 className="font-display text-3xl">Supplier hub</h1>
        <div className="mt-4 rounded-xl border border-dashed border-zinc-300 p-10 text-center text-zinc-500">
          Your account isn't linked to a marketplace supplier yet. Ask the Seavanto admin to link your login to your company profile.
        </div>
      </div>
    );
  }
  const { stats, invited = [], quoted = [], won = [], declined = [], reviews = [], suppliers = [] } = data;
  const profile = suppliers[0];
  const API = process.env.REACT_APP_BACKEND_URL;
  const imgSrc = (p) => (p && /^https?:\/\//i.test(p) ? p : `${API}/files/${p}`);

  return (
    <div className="p-6 lg:p-10 max-w-5xl" data-testid="supplier-dashboard">
      <h1 className="font-display text-3xl">Supplier hub</h1>
      <p className="text-sm text-zinc-500 mt-1">Manage incoming requests, quotes and jobs.</p>

      {data.bank_details_missing && !ibanDismissed && (
        <div className="mt-4 flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 p-3" data-testid="iban-nudge-banner">
          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <div className="flex-1 text-sm text-amber-900 min-w-0">
            <span className="font-semibold">Add your bank details (IBAN)</span> — prepayments and payouts stay blocked until the yacht knows where to send the money.
          </div>
          <Button size="sm" className="bg-ink hover:bg-black shrink-0" onClick={() => { window.location.href = "/app/profile"; }} data-testid="iban-nudge-open-profile">Open profile</Button>
          <button
            type="button"
            className="text-amber-500 hover:text-amber-800 shrink-0 mt-1"
            title="Dismiss"
            onClick={() => { try { localStorage.setItem("iban_nudge_dismissed", "1"); } catch {} setIbanDismissed(true); }}
            data-testid="iban-nudge-dismiss"
          ><X className="h-4 w-4" /></button>
        </div>
      )}

      {profile && (
        <section className="mt-6 rounded-2xl border border-zinc-200 bg-white overflow-hidden" data-testid="supplier-profile">
          <div className="h-28 bg-gradient-to-br from-sky-100 to-zinc-100 relative">
            {profile.cover_image && <img src={imgSrc(profile.cover_image)} alt="" className="w-full h-full object-cover" />}
            <span className="absolute top-3 right-3 text-[10px] uppercase tracking-wider bg-white/90 text-zinc-700 px-2 py-0.5 rounded-full capitalize" data-testid="supplier-profile-status">{profile.status}</span>
          </div>
          <div className="p-5">
            <div className="flex items-start gap-4 -mt-12">
              <div className="relative z-10 h-16 w-16 rounded-xl bg-white ring-1 ring-zinc-200 shadow-sm overflow-hidden shrink-0 flex items-center justify-center">
                {profile.logo ? <img src={imgSrc(profile.logo)} alt="" className="h-full w-full object-cover" /> : <Star className="h-6 w-6 text-zinc-300" />}
              </div>
              <div className="pt-12 min-w-0">
                <div className="font-display text-2xl truncate">{profile.company_name}</div>
                <div className="text-sm text-zinc-500">{[profile.city, profile.country].filter(Boolean).join(", ") || "—"}{profile.marina ? ` · ${profile.marina}` : ""}</div>
              </div>
            </div>
            {profile.description && <p className="mt-3 text-sm text-zinc-700">{profile.description}</p>}

            <div className="mt-4 flex flex-wrap gap-1.5">
              {(profile.categories || []).map((c) => <Badge key={c.id} variant="secondary" className="font-normal">{c.name}</Badge>)}
              {(profile.specialties || []).map((s) => <Badge key={s.id} variant="outline" className="font-normal">{s.name}</Badge>)}
            </div>

            {(profile.services || []).length > 0 && (
              <div className="mt-4">
                <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Services</div>
                <ul className="text-sm text-zinc-700 space-y-1">
                  {profile.services.map((sv, i) => (
                    <li key={i} className="flex items-center justify-between gap-3 border-b border-zinc-50 pb-1">
                      <span>{sv.name}</span>
                      {sv.starting_price != null && <span className="text-zinc-500 text-xs whitespace-nowrap">from {sv.currency || ""} {sv.starting_price}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-xs text-zinc-500">
              <span><span className="text-zinc-400">Rating</span> · {profile.average_rating || "—"} ({profile.review_count || 0})</span>
              <span><span className="text-zinc-400">Jobs completed</span> · {profile.completed_jobs || 0}</span>
              {profile.response_time && <span><span className="text-zinc-400">Response</span> · {profile.response_time}</span>}
              {profile.emergency_247 && <span className="text-rose-600">24/7 emergency</span>}
              {(profile.languages || []).length > 0 && <span><span className="text-zinc-400">Languages</span> · {profile.languages.join(", ")}</span>}
            </div>
            {user?.supplier_role !== "manager" && (
              <p className="mt-3 text-[11px] text-zinc-400">Edit your listing under My profile → Company details.</p>
            )}
          </div>
        </section>
      )}

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={Inbox} label="New requests" value={stats.open_requests} testid="stat-open" />
        <Stat icon={Clock} label="Pending quotes" value={stats.pending_quotes} testid="stat-pending" />
        <Stat icon={Trophy} label="Jobs won" value={stats.jobs_won} testid="stat-won" />
        <Stat icon={Star} label="Rating" value={stats.average_rating || "—"} testid="stat-rating" />
      </div>

      <section className="mt-8">
        <h2 className="font-display text-xl mb-2 flex items-center gap-2 text-red-600">
          New requests
          {invited.length > 0 && <span className="rounded-full bg-red-100 text-red-700 text-xs font-semibold px-2 py-0.5">{invited.length}</span>}
        </h2>
        {invited.length === 0 ? <p className="text-sm text-zinc-500">No new requests.</p> :
          <div className="space-y-2">{invited.map((q) => <RequestCard key={q.id} q={q} onChange={load} localCurrency={localCurrencyFor(profile?.country)} />)}</div>}
      </section>

      <section className="mt-8">
        <h2 className="font-display text-xl mb-2" data-testid="awaiting-decision-heading">Awaiting decision ({quoted.length})</h2>
        {quoted.length === 0 ? <p className="text-sm text-zinc-500">No pending quotes.</p> :
          <div className="space-y-2">{quoted.map((q) => <RequestCard key={q.id} q={q} onChange={load} localCurrency={localCurrencyFor(profile?.country)} />)}</div>}
      </section>

      <CollapsedSection title="Won jobs" count={won.length} testid="won-jobs-section">
        {won.length === 0 ? <p className="text-sm text-zinc-500">No won jobs yet.</p> :
          <div className="space-y-2">{won.map((q) => <RequestCard key={q.id} q={q} onChange={load} />)}</div>}
      </CollapsedSection>

      {declined.length > 0 && (
        <CollapsedSection title="Declined jobs" count={declined.length} testid="declined-jobs-section">
          <p className="text-xs text-zinc-500 mb-2">Requests the yacht declined or where another supplier was chosen — kept here for your records.</p>
          <div className="space-y-2">{declined.map((q) => <RequestCard key={q.id} q={q} onChange={load} />)}</div>
        </CollapsedSection>
      )}

      {reviews.length > 0 && (
        <section className="mt-8">
          <h2 className="font-display text-xl mb-2">Recent reviews</h2>
          <div className="space-y-2">
            {reviews.map((r) => (
              <div key={r.id} className="rounded-lg border border-zinc-200 p-3" data-testid={`sup-review-${r.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1 text-amber-500">{Array.from({ length: r.rating }).map((_, i) => <Star key={i} className="h-3.5 w-3.5 fill-amber-400 stroke-amber-400" />)}</div>
                  <span className="text-[11px] text-zinc-400">{(r.created_at || "").slice(0, 10)}</span>
                </div>
                {(r.yacht_name || r.task_title) && (
                  <div className="text-xs text-zinc-500 mt-0.5">{[r.yacht_name, r.task_title].filter(Boolean).join(" · ")}</div>
                )}
                {r.comment && <p className="text-sm text-zinc-600 mt-1">{r.comment}</p>}
                <div className="text-xs text-zinc-400 mt-1">{r.reviewer_name}</div>
                <ReviewReplyForm review={r} onReplied={load} />
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
